import {
  Stack,
  Text,
  Heading,
  Column,
  Columns,
  IconEdit,
  Inline,
  Box,
  IconBolt,
  Skeleton,
  ButtonIcon,
  IconVisibility,
  IconVisibilityOff,
  useToast,
  IconWarningAmber,
  TextLink,
  useResponsiveValue,
  IconInfoInverted,
  Badge,
  PopoverRenderer
} from '@machobear/design-system'
import { useParams } from 'react-router-dom'
import { isBefore, parse, endOfMonth, endOfDay } from 'date-fns'
import { pipe, reject, join } from 'ramda'
import { format, parseISO } from 'date-fns'
import { useHistory } from 'react-router'
import { vars } from '@machobear/design-system/css'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Userpilot } from 'userpilot'
import queryClient from '@/components/query-client'
import { isFalsy } from '@/utils'
import prices from '@/lib/prices'
import { openBlobInNewTab, dataURItoBlob } from '@/utils/files'
import {
  useGetPaymentMethod,
  useGetProduct,
  useAvailableCreditChecks
} from '@/hooks/use-block-and-pay'
import capitalize from '@/utils/capitalize'
import {
  useGetTenantConsent,
  useHandleStandAlonePurchase,
  useUnlockStandaloneTrustScore,
  useGetStandAloneTrustScoreSin,
  useGetStandAloneTrustScoreDl
} from '@/hooks/use-standalone-trust-score'
import config from '@/config'
import { FormBox } from './form-sections'
import FormFooter from './form-footer'
import { useFormContext } from './form-context'

const { WEB_URL } = config
const Confirmation = ({
  data = {},
  isLoading,
  hasAvailableCreditChecks
}: {
  data: any
  isLoading: boolean
  hasAvailableCreditChecks: boolean
}) => {
  const { t } = useTranslation()
  const { couponCode, temporaryPaymentMethod, isReadyToSubmit } = useFormContext()
  const showToast = useToast()
  const { tenant = {}, trustworthiness = {}, rent_to_income = {} } = data
  const { trustScoreId = '' } = useParams<{ trustScoreId: string }>()
  const { data: tenantConsentData, isLoading: isLoadingConsent } = useGetTenantConsent(trustScoreId)
  const { data: paymentMethods, isLoading: isLoadingPaymentMethod } =
    useGetPaymentMethod('paymentMethod')
  const { availableCreditChecks, creditDetails } = useAvailableCreditChecks()
  const { mutateAsync: handleStandAlonePurchase, isPending: isPurchasing } =
    useHandleStandAlonePurchase()
  const { mutateAsync: unlockStandaloneTrustScore, isPending: isUnlocking } =
    useUnlockStandaloneTrustScore(trustScoreId, creditDetails)

  const { mutateAsync: getStandAloneTrustScoreSin, isPending: isLoadingSin } =
    useGetStandAloneTrustScoreSin()
  const { mutateAsync: getStandAloneTrustScoreDl, isPending: isLoadingDl } =
    useGetStandAloneTrustScoreDl()

  const history = useHistory()
  const paymentMehod = paymentMethods?.[0] || temporaryPaymentMethod[0]

  const { data: product, isLoading: loadingPackage } = useGetProduct(
    'credit-check-single-package',
    { couponCode, state: paymentMehod?.state }
  )

  const [sinInfo, setSinInfo] = useState({ value: '', visible: false })
  const [dlInfo, setDlInfo] = useState({ value: '', visible: false })

  const handleSinVisibilityChange = async () => {
    if (!sinInfo.visible && sinInfo.value === '') {
      const result = await getStandAloneTrustScoreSin(trustScoreId)
      return setSinInfo({ value: result.sin, visible: true })
    }

    if (!sinInfo.visible) {
      return setSinInfo({ value: sinInfo.value, visible: true })
    }

    return setSinInfo({ value: sinInfo.value, visible: false })
  }

  const handleDlVisibilityChange = async () => {
    if (!dlInfo.visible && dlInfo.value === '') {
      const result = await getStandAloneTrustScoreDl(trustScoreId)
      return setDlInfo({ value: result.driver_license, visible: true })
    }

    if (!dlInfo.visible) {
      return setDlInfo({ value: dlInfo.value, visible: true })
    }

    return setDlInfo({ value: dlInfo.value, visible: false })
  }

  const handleTrustScoreSubmit = async () => {
    Userpilot.track('app_standalone_ts_form_confirm_submit_btn')
    if (!isReadyToSubmit) {
      showToast({
        message: 'Please complete all required fields',
        tone: 'critical'
      })
      return
    }

    if (!hasAvailableCreditChecks) {
      await handleStandAlonePurchase({
        paymentMethodId: paymentMehod.payment_method_id,
        discountCode: couponCode,
        onSuccess: async () => {
          await unlockStandaloneTrustScore({
            onSuccess: () => {
              Userpilot.track('goal_purchase_standalone_trust_score_unlock', {
                price: prices.trustScore.unlock
              })
              showToast({ message: 'Unlocked the trust score', tone: 'positive' })
            }
          })
          history.push('/trust-score')
        }
      })
    } else {
      await unlockStandaloneTrustScore({
        onSuccess: async () => {
          Userpilot.track('goal_purchase_credits_standalone_trust_score_unlock', {
            price: prices.trustScore.unlock
          })
          localStorage.removeItem('promoType')
          queryClient.invalidateQueries({ queryKey: ['trustScoreList'] })
          showToast({ message: 'Unlocked the trust score', tone: 'positive' })
        }
      })
      history.push('/trust-score')
    }
  }

  const formatDate = (date: string) => {
    const parsedDate = parseISO(date)
    const formattedDate = format(parsedDate, 'dd/MM/yyyy')
    return formattedDate
  }

  const formatAddress = (address: {
    unit_number: string | null
    street_number: string | null
    street_name: string | null
    city: string | null
    state: string | null
    postal_code: string | null
  }) => {
    const unit = address?.unit_number ? `${address.unit_number} - ` : ''
    const rest = pipe(
      reject(isFalsy),
      join(', ')
    )([
      address?.street_number,
      address?.street_name,
      address?.city,
      address?.state,
      address?.postal_code
    ])
    return `${unit}${rest}`
  }

  const currentDate = new Date()
  const expirationDate =
    paymentMehod?.expiration_date &&
    endOfDay(endOfMonth(parse(paymentMehod.expiration_date, 'MMyy', new Date())))

  const isCreditCardExpired = isBefore(expirationDate, currentDate)

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })
  return (
    <Stack align="left" space="gutter">
      {!isMobile && (
        <Heading level="4">
          {t('l.standalone_trustscore.form.step_confirmation_form', 'Confirmation Form')}
        </Heading>
      )}
      <FormBox
        label={t('r.profile.edit.credit_check.title', 'Credit Check')}
        secondaryLabel={
          <EditButton
            label="basic information"
            onClick={() => history.push(`/trust-score/edit/${trustScoreId}/credit-check`)}
            disabled={isPurchasing || isUnlocking}
          />
        }
      >
        <Stack space={'gutter'}>
          <Text size="medium" weight="medium">
            {t('r.profile.edit.basic_information.title', 'Basic Information')}
          </Text>
          <ListRow isLoading={isLoading} label={t('l.full_legal_name.text', 'Full legal name')}>
            <Text weight="medium">{`${tenant.basic_info?.first_name} ${tenant.basic_info?.last_name}`}</Text>
          </ListRow>
          <ListRow isLoading={isLoading} label={t('l.date_of_birth.text', 'Date of birth')}>
            <Text weight="medium">
              {tenant.basic_info?.dob ? formatDate(tenant.basic_info.dob) : '-'}
            </Text>
          </ListRow>
          <ListRow
            isLoading={isLoading}
            label={t('r.profile.view.current_address.label', 'Current Address')}
          >
            <Text weight="medium">
              {tenant.address?.city !== null ? formatAddress(tenant.address) : '-'}
            </Text>
          </ListRow>
          <Divider />
          <Text size="medium" weight="medium">
            {t('r.profile.edit.identification_documents.label', 'Identification Documents')}
          </Text>
          <ListRow
            isLoading={isLoading}
            label={t('l.standalone_trustscore.form.identity_sin', 'Social insurance number')}
          >
            {data?.tenant?.identity?.sin ? (
              <Inline space={'xxsmall'} alignY={'center'}>
                <Text weight="medium">
                  {sinInfo.visible ? sinInfo.value : data.tenant.identity.sin}
                </Text>
                {isMobile ? (
                  <Box onClick={handleSinVisibilityChange} cursor="pointer">
                    <Text weight="medium" tone="link">
                      {sinInfo.visible
                        ? t('l.tenants.view.hide.button', 'Hide')
                        : t('l.tenants.view.reveal.button', 'Reveal')}
                    </Text>
                  </Box>
                ) : (
                  <ButtonIcon
                    icon={sinInfo.visible ? <IconVisibilityOff /> : <IconVisibility />}
                    id="sin-visibility-on"
                    label={
                      sinInfo.visible
                        ? t('l.standalone_trustscore.form.hide_sin.label', 'Hide SIN')
                        : t('l.standalone_trustscore.form.show_sin.label', 'Show SIN')
                    }
                    onClick={handleSinVisibilityChange}
                  />
                )}
              </Inline>
            ) : isLoadingSin ? (
              <Skeleton.Shape height={24} />
            ) : (
              <Text>-</Text>
            )}
          </ListRow>
          <ListRow
            isLoading={isLoading}
            label={t('l.standalone_trustscore.form.dl_number', "Driver's licence number")}
          >
            {data?.tenant?.identity?.driver_license && !isLoadingDl ? (
              <Inline space={'xxsmall'} alignY={'center'}>
                <Text weight="medium">
                  {dlInfo.visible ? dlInfo.value : data.tenant.identity.driver_license}
                </Text>
                {isMobile ? (
                  <Box onClick={handleDlVisibilityChange} cursor="pointer">
                    <Text weight="medium" tone="link">
                      {dlInfo.visible
                        ? t('l.tenants.view.hide.button', 'Hide')
                        : t('l.tenants.view.reveal.button', 'Reveal')}
                    </Text>
                  </Box>
                ) : (
                  <ButtonIcon
                    icon={dlInfo.visible ? <IconVisibilityOff /> : <IconVisibility />}
                    id="sin-visibility-on"
                    label={
                      dlInfo.visible
                        ? t(
                            'l.standalone_trustscore.form.hide_dl.label',
                            'Hide Drivers licence number'
                          )
                        : t(
                            'l.standalone_trustscore.form.show_dl.label',
                            'Show Drivers licence number'
                          )
                    }
                    onClick={handleDlVisibilityChange}
                  />
                )}
              </Inline>
            ) : isLoadingDl ? (
              <Skeleton.Shape height={24} />
            ) : (
              <Text>-</Text>
            )}
          </ListRow>
          {!data?.tenant?.identity?.sin && !data?.tenant?.identity?.driver_license && (
            <Box padding="small" background="cautionLight" borderRadius={'large'}>
              <Columns space="xxsmall" alignY="top">
                <Column width="content">
                  <IconWarningAmber tone="caution" />
                </Column>
                <Column>
                  <Text size="small" tone="caution">
                    {t(
                      'l.trust_score_auth.identification_document.error',
                      "Without submitted identification documents, the Credit Summary section in this applicant's Trust Score report may lack accuracy in confirming a match with Equifax® records."
                    )}
                  </Text>
                </Column>
              </Columns>
            </Box>
          )}
          <Divider />
          <Text size="medium" weight="medium">
            {t('l.standalone_trustscore.form.tenant_consent_title', 'Tenant Consent')}
          </Text>
          <ListRow
            isLoading={isLoadingConsent}
            label={t('r.profile.view.document.text', 'Document')}
          >
            {tenantConsentData?.consent && !isLoadingConsent ? (
              <Box
                aria-label="View document"
                onClick={() => openBlobInNewTab(dataURItoBlob(tenantConsentData.consent))}
                cursor="pointer"
                role="button"
              >
                <Text size="medium" weight="medium" tone="link">
                  {t('l.listing.application.view.text', 'View')}
                </Text>
              </Box>
            ) : (
              <Text>-</Text>
            )}
          </ListRow>
        </Stack>
      </FormBox>

      <FormBox
        label={t('l.credit_usage.sidemenu.tenant_screening.label', 'Tenant Screening')}
        secondaryLabel={
          <EditButton
            label="trustworthiness"
            onClick={() => history.push(`/trust-score/edit/${trustScoreId}/tenant-screening`)}
            disabled={isPurchasing || isUnlocking}
          />
        }
      >
        <Stack space={'gutter'}>
          <Text size="medium" weight="medium">
            {t('l.application.view.trustworthiness.title', 'Trustworthiness')}
          </Text>
          <ListRow
            isLoading={isLoading}
            label={t(
              'l.standalone_trustscore.form.trustworthiness_id_provided',
              'Did the tenant provide a valid ID?'
            )}
          >
            <Text weight="medium">
              {trustworthiness.id_provided === null
                ? '-'
                : trustworthiness.id_provided
                  ? t('common.yes', 'Yes')
                  : t('common.no', 'No')}
            </Text>
          </ListRow>
          <ListRow
            isLoading={isLoading}
            label={t(
              'l.standalone_trustscore.form.trustworthiness_linkedin_provided',
              'Did the tenant provide a LinkedIn profile?'
            )}
          >
            <Text weight="medium">
              {trustworthiness.linkedin_provided === null
                ? '-'
                : trustworthiness.linkedin_provided === 'does_not_matter'
                  ? t(
                      'l.standalone_trustscore.form.trustworthiness_linkeindin',
                      "This doesn't matter to me"
                    )
                  : trustworthiness.linkedin_provided === 'yes'
                    ? t('common.yes', 'Yes')
                    : t('common.no', 'No')}
            </Text>
          </ListRow>
          <ListRow
            isLoading={isLoading}
            label={t(
              'l.standalone_trustscore.form.trustworthiness_employment_provided',
              'Did the tenant provide proof of employment?'
            )}
          >
            <Text weight="medium">
              {trustworthiness.employment_provided === null
                ? '-'
                : trustworthiness.employment_provided
                  ? t('common.yes', 'Yes')
                  : t('common.no', 'No')}
            </Text>
          </ListRow>
          <ListRow
            isLoading={isLoading}
            label={t(
              'l.standalone_trustscore.form.trustworthiness_proof_income',
              'Did the tenant provide proof of income?'
            )}
          >
            <Text weight="medium">
              {trustworthiness.income_provided === null
                ? '-'
                : trustworthiness.income_provided
                  ? t('common.yes', 'Yes')
                  : t('common.no', 'No')}
            </Text>
          </ListRow>
          <Divider />
          <Text size="medium" weight="medium">
            {t('l.standalone_trustscore.form.rent_to_income_title', 'Rent-to-Income Ratio')}
          </Text>
          <ListRow
            isLoading={isLoading}
            label={t(
              'l.standalone_trustscore.form.rent_to_income_listing_price',
              'What is your listing’s rental price?'
            )}
          >
            <Text weight="medium">
              {rent_to_income.listing_price
                ? `$${parseInt(rent_to_income.listing_price).toLocaleString()}`
                : '-'}
            </Text>
          </ListRow>
          <ListRow
            isLoading={isLoading}
            label={t(
              'l.standalone_trustscore.form.rent_to_income_household_income',
              'What is the total household annual income?'
            )}
          >
            <Text weight="medium">
              {rent_to_income.household_income
                ? `$${parseInt(rent_to_income.household_income).toLocaleString()}`
                : '-'}
            </Text>
          </ListRow>
        </Stack>
      </FormBox>

      <FormBox
        label={t('l.standalone_trustscore.form.basic_payment_title', 'Payment')}
        secondaryLabel={
          !hasAvailableCreditChecks ? (
            <EditButton
              label="payment"
              onClick={() => history.push(`/trust-score/edit/${trustScoreId}/payment`)}
              disabled={isPurchasing || isUnlocking}
            />
          ) : (
            <></>
          )
        }
      >
        <Stack space={'gutter'}>
          {hasAvailableCreditChecks ? (
            <>
              <ListRow
                disableCollapse
                isLoading={isLoading}
                label="Tenant Screening credits"
                tooltip={
                  <Inline space="small">
                    <PopoverRenderer
                      trigger={({ triggerProps }) => (
                        <Box component="span" aria-label="Trigger" {...triggerProps}>
                          <IconInfoInverted />
                        </Box>
                      )}
                    >
                      {({ close }) => (
                        <Box paddingTop={'small'}>
                          <Stack space="xxsmall">
                            <Text>
                              {t(
                                'l.standalone_trustscore.form.already_purchased_credits_tooltip',
                                'You’ve previously purchased or have Tenant Screening credits included in your plan - making it easier to use premium features.'
                              )}
                            </Text>
                            <Box
                              component="a"
                              href="https://liv.rent/blog/livrent/credit-usage-for-landlords/"
                              target="_blank"
                              role="link"
                              style={{
                                textDecoration: 'underline',
                                color: vars.foregroundColor.white,
                                fontWeight: vars.textWeight.medium
                              }}
                            >
                              {t('common.learn_more', 'Learn more')}
                            </Box>
                          </Stack>
                        </Box>
                      )}
                    </PopoverRenderer>
                  </Inline>
                }
              >
                <Text weight="medium">{availableCreditChecks}</Text>
              </ListRow>
              <ListRow isLoading={isLoading} label="Credits used on this purchase" disableCollapse>
                <Text weight="medium">1</Text>
              </ListRow>
              <ListRow isLoading={isLoading} label="Remaining credits" disableCollapse>
                <Text weight="medium">{availableCreditChecks - 1}</Text>
              </ListRow>

              <Box padding="small" background="promoteLight" borderRadius="large">
                <Columns space="xxsmall" alignY={{ mobile: 'top', tablet: 'center' }}>
                  <Column width="content">
                    <IconBolt tone="promote" />
                  </Column>
                  <Column>
                    <Trans i18nKey="l.standalone_trustscore.form.unlock_premium" t={t}>
                      <Text weight="medium" tone="promote" size="small">
                        Unlock premium marketing & tenant screening features.{' '}
                        <TextLink
                          target="_blank"
                          href={`${WEB_URL}/app/usage-credits/products/all`}
                        >
                          Learn more
                        </TextLink>
                      </Text>
                    </Trans>
                  </Column>
                </Columns>
              </Box>
            </>
          ) : (
            <>
              <ListRow
                isLoading={loadingPackage}
                label={t('r.payment.view.payment_confirmation_modal_total.text', 'Total')}
                disableCollapse
              >
                <Text weight="medium" align={{ mobile: 'right', tablet: 'left' }}>
                  CAD ${product?.products?.[0].total_due}
                </Text>
              </ListRow>
              <ListRow
                isLoading={isLoadingPaymentMethod}
                label={t('b.payment.view.payment_method.label', 'Payment method')}
                disableCollapse
              >
                <Inline space="xxsmall" alignY="center" align={{ mobile: 'right', tablet: 'left' }}>
                  <Text weight="medium">
                    {paymentMehod?.brand
                      ? `${capitalize(paymentMehod?.brand)} **** ${paymentMehod?.card_number}`
                      : '-'}
                  </Text>
                  {isCreditCardExpired && !isMobile && <Badge tone="critical">Expired</Badge>}
                </Inline>
              </ListRow>

              {isCreditCardExpired && isMobile && (
                <Inline align="right" space="none">
                  <Badge tone="critical">
                    {t('r.payment.edit.payment_method.expired.badge.title', 'Expired')}
                  </Badge>
                </Inline>
              )}

              <Trans i18nKey="l.standalone_trustscore.form.payment_confirmation" t={t}>
                <Text weight="medium" size="xsmall" tone="tertiary">
                  By clicking “Submit”, I agree to a one-time charge and confirm that I have read
                  and agree to liv.rent's{' '}
                  <TextLink href={`${WEB_URL}/privacy`} target="_blank" weight="regular">
                    Privacy Policy
                  </TextLink>{' '}
                  and{' '}
                  <TextLink target="_blank" href={`${WEB_URL}/terms`} weight="regular">
                    Terms & Conditions.
                  </TextLink>
                </Text>
              </Trans>
              <Box padding="small" background="promoteLight" borderRadius="large">
                <Columns space="xxsmall" alignY={{ mobile: 'top', tablet: 'center' }}>
                  <Column width="content">
                    <IconBolt tone="promote" />
                  </Column>
                  <Column>
                    <Trans i18nKey="l.standalone_trustscore.form.speedup_promotion" t={t}>
                      <Text weight="medium" tone="promote" size="small">
                        Want to speed up your workflow? Get Tenant Screening credits.{' '}
                        <TextLink
                          href={`${WEB_URL}/app/usage-credits/products/tenant-screening`}
                          target="_blank"
                        >
                          Learn more
                        </TextLink>
                      </Text>
                    </Trans>
                  </Column>
                </Columns>
              </Box>
            </>
          )}
        </Stack>
      </FormBox>

      <FormBox label={t('l.credit_check_authorization', 'Credit Check Authorization')}>
        <Stack space={'gutter'}>
          <Text size="small">
            {t(
              'l.standalone_trustscore.form.confirmation_warning1',
              'By clicking “Submit”, I authorize liv.rent to submit an Equifax® credit inquiry based on the information provided above.'
            )}
          </Text>

          <Text size="small">
            {t(
              'l.standalone_trustscore.form.confirmation_warning2',
              'I acknowledge and agree to ensure the accuracy of this information. liv.rent bears no responsibility for any failed credit checks resulting from inaccuracies in the provided tenant details.'
            )}
          </Text>
        </Stack>
      </FormBox>

      <FormFooter
        onSubmit={handleTrustScoreSubmit}
        showPayment={!hasAvailableCreditChecks}
        isSubmitting={isPurchasing || isUnlocking}
        isLoading={isLoading}
      />
    </Stack>
  )
}

const Divider = () => {
  return (
    <Box width="full" style={{ height: '1px', backgroundColor: vars.borderColor.neutralLight }} />
  )
}

const EditButton = ({
  onClick,
  label,
  disabled
}: {
  onClick: () => void
  label: string
  disabled: boolean
}) => {
  const { t } = useTranslation()
  return (
    <Box
      role="button"
      onClick={onClick}
      cursor="pointer"
      aria-label={`edit ${label}`}
      disabled={disabled}
    >
      <Inline space="xxsmall" alignY="center">
        <IconEdit tone={disabled ? 'disabled' : 'link'} size="xsmall" />
        <Text tone={disabled ? 'disabled' : 'link'} size="medium" weight="medium">
          {t('common.edit', 'Edit')}
        </Text>
      </Inline>
    </Box>
  )
}

const ListRow = ({
  label,
  children,
  isLoading,
  disableCollapse = false,
  tooltip
}: {
  label: string
  children: any
  isLoading?: boolean
  disableCollapse?: boolean
  tooltip?: any
}) => {
  return (
    <Columns space={'gutter'} collapseBelow={disableCollapse ? undefined : 'tablet'}>
      <Column width="1/2">
        <Inline space="xxsmall" alignY="center">
          <Text weight="medium" tone="tertiary">
            {label}
          </Text>
          {tooltip ? tooltip : null}
        </Inline>
      </Column>
      <Column width="1/2">{isLoading ? <Skeleton.Shape height={24} /> : children}</Column>
    </Columns>
  )
}

export default Confirmation
